// @ts-strict-ignore
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Injector,
	Input,
	IterableDiffers,
	OnChanges,
	OnDestroy,
	output,
	Output,
	SimpleChanges,
	ViewChild,
} from "@angular/core";
import {BaseBookmarkPane} from "../base/bookmark-pane-base";
import {TranslateService} from "@ngx-translate/core";
import {animate, style, transition, trigger} from "@angular/animations";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {
	DataPermissions,
	DataPermissionType,
	Icon,
	Marker,
	MarkerType,
	MediaMarker,
	PlayerMapping,
	ScenarioRecordingState,
	TabletMapping,
	TrainingSessionUserData,
	ViewingStationMapping,
	WebClientType,
} from "@models";
import {SimConfigurationService, SimDeviceMappingService, SimPermissionService, SimUserExtendedService, TrackByMethods} from "@services";
import {EventEmitterService} from "@event-emitter";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {Translations} from "@translations";
import Enumerable from "linq";

export const ICONS_WITHOUT_FILTER = [
	"thumbsup",
	"exclamationtriangle",
	"bubbleblue",
	"bubblered",
	"bubblegreen",
	"decisionblue",
	"decisionred",
	"decisiongreen",
	"decisionwhite",
	"markerpicflame",
	"markerpicexplode",
];

@Component({
	selector: "sim-bookmark-pane",
	templateUrl: "./bookmark-pane.component.html",
	styleUrls: ["./bookmark-pane.component.scss"],
	animations: [
		trigger("fadeIn", [
			transition(":enter", [
				style({
					opacity: "0",
					height: "0px",
				}),
				animate(
					".175s ease-out",
					style({
						opacity: "1",
						height: "75px",
					}),
				),
			]),
		]),
		trigger("fadeOut", [
			transition(":leave", [
				style({
					opacity: "1",
					height: "75px",
				}),
				animate(
					".175s ease-out",
					style({
						opacity: "0",
						height: "0px",
					}),
				),
			]),
		]),
		zoomInOnEnterAnimation({duration: 175}),
		zoomOutOnLeaveAnimation({duration: 175}),
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [EventEmitterService],
	standalone: false,
})
export class BookmarkPaneComponent extends BaseBookmarkPane implements OnChanges, OnDestroy {
	T = Translations;
	@ViewChild("bookmarkScrollContainer", {static: false})
	bookmarkScrollContainer: CdkVirtualScrollViewport;

	MarkerType = MarkerType;
	ScenarioRecordingState = ScenarioRecordingState;
	TrackByMethods = TrackByMethods;

	@Input() mapping: TabletMapping | ViewingStationMapping;
	@Input() playbackPosition: number = null;
	@Input() onlyFavs = false;
	@Input() playerInfo: PlayerMapping;
	@Input() isDebriefing = false;
	@Input() isArchive = false;
	@Input() activeBookmarkOfUserId: string = null;
	@Input() archivePosition: number;
	@Input() showMarkersetPane = true;
	@Input() trainingSessionUserData: {[key: string]: TrainingSessionUserData};
	@Input() panelOptions: {
		isUndockedPanel: boolean;
		isInUndockedState: boolean;
	};
	mediaMarkerClicked = output<string>();
	@Input() isRecording: boolean = false;

	@Output() jumpToMarker: EventEmitter<Marker> = new EventEmitter<Marker>();

	public settings = SimConfigurationService.settings;

	public markerIdInEditMode: string = "";
	public markerEditModeValue: string = "";
	private allowMarkersOutOfFocus: boolean = true;

	public get user() {
		return this._simUserService.user;
	}

	constructor(
		private injector: Injector,
		protected differs: IterableDiffers,
		public translate: TranslateService,
		public _simUserService: SimUserExtendedService,
		public _simPermissionService: SimPermissionService,
		private _emitterService: EventEmitterService,
		protected changeDetector: ChangeDetectorRef,
	) {
		super(injector, differs, changeDetector, _emitterService);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		super.ngOnChanges(changes);
	}

	public addMarker(icon: Icon) {
		if (this.scenarioRecording) {
			let position;
			if (this.isDebriefing) {
				position = this.playerInfo?.info?.mediaPosition;
			} else if (this.isRecording) {
				position = this.playbackPosition;
			} else if (this.isArchive) {
				position = this.archivePosition === 0 ? 1 : this.archivePosition;
			}

			let clientType: WebClientType;

			if (SimDeviceMappingService.clientProperties().isRecorder) {
				clientType = WebClientType.Recorder;
			} else if (SimDeviceMappingService.clientProperties().isTablet) {
				clientType = WebClientType.Tablet;
			} else if (SimDeviceMappingService.clientProperties().isViewingStation) {
				clientType = WebClientType.ViewingStation;
			}

			this._simRecorderService
				.addMarkerToActiveRecording({
					scenarioRecordingId: this.scenarioRecording.id,
					icon: icon,
					userId: this._simUserService.user.id,
					position: position,
					clientType: clientType,
				})
				.then((addMarkerToActiveRecordingResult) => {
					if (!addMarkerToActiveRecordingResult) {
						this._simNotificationService.notifyError(this.translate.instant(this.T.RECORDER_TXT_ADD_MARKER_ERROR));
					} else {
						this.setMarkerToEditMode(addMarkerToActiveRecordingResult.modifiedMarker);
					}
				});
		} else {
			this._simNotificationService.notifyInfo(this.translate.instant(this.T.RECORDER_TXT_NO_RECORDING_STARTED));
		}
	}

	public toggleMarkerEditMode(marker: Marker, textElementRef: HTMLTextAreaElement) {
		this.allowMarkersOutOfFocus = false;
		this.setMarkerToEditMode(marker);
		this.allowMarkersOutOfFocus = true;
		setTimeout(() => {
			textElementRef.focus();
		}, 100);
	}

	public setMarkerToEditMode(marker: Marker) {
		if (this.markerIdInEditMode !== marker?.markerId && marker) {
			if (!!this.markerIdInEditMode) {
				this.updateMarkerText(this.markerIdInEditMode, this.markerEditModeValue);
			}
			this.markerIdInEditMode = marker?.markerId ?? "";
			this.markerEditModeValue = marker?.text ?? "";
		} else if (marker === null) {
			this.updateMarkerText(this.markerIdInEditMode, this.markerEditModeValue);
			this.markerIdInEditMode = "";
		}
	}

	private async updateMarkerText(markerId: string, text: string) {
		//update internal marker
		let marker = Enumerable.from(this.scenarioRecording?.markers).firstOrDefault((x) => x.markerId == markerId);
		if (marker) {
			marker.text = text;
			await this._simRecorderService.updateMarkerTextToActiveRecording({
				scenarioRecordingId: this.scenarioRecording.id,
				markerId: markerId,
				markerText: text,
			});
		}
	}

	public adjustTextarea(textAreaElement: HTMLTextAreaElement) {
		textAreaElement.style.height = "";
		setTimeout(() => {
			textAreaElement.style.height = Math.min(textAreaElement.scrollHeight) + "px";
		}, 100);
	}

	emitMediaMarkerClicked(marker: Marker) {
		let mediaMarker = <MediaMarker>marker;
		if (mediaMarker.mediaId) {
			this.mediaMarkerClicked.emit(mediaMarker.mediaId);
		}
	}

	showMarkerActive(markerIndex: number) {
		if (!this.isRecording) {
			if (this.isDebriefing) {
				if (this.lastClickedMarkerId) {
					if (this.lastClickedMarkerId === this.scenarioRecording?.markers[markerIndex].markerId) {
						if (this.playerInfo?.info?.mediaPosition <= this.scenarioRecording?.markers[markerIndex].position) {
							return true;
						} else {
							this.lastClickedMarkerId = null;
							return false;
						}
					} else {
						return false;
					}
				} else {
					if (markerIndex === this.scenarioRecording?.markers?.length - 1) {
						return this.playerInfo?.info?.mediaPosition >= this.scenarioRecording?.markers[markerIndex].position;
					}
					return (
						this.playerInfo?.info?.mediaPosition >= this.scenarioRecording?.markers[markerIndex].position &&
						this.playerInfo?.info?.mediaPosition < this.scenarioRecording?.markers[markerIndex + 1].position
					);
				}
			} else if (this.isArchive) {
				if (this.lastClickedMarkerId) {
					if (this.lastClickedMarkerId === this.scenarioRecording?.markers[markerIndex].markerId) {
						if (this.archivePosition <= this.scenarioRecording?.markers[markerIndex].position) {
							return true;
						} else {
							this.lastClickedMarkerId = null;
							return false;
						}
					} else {
						return false;
					}
				} else {
					if (markerIndex === this.scenarioRecording?.markers?.length - 1) {
						return this.archivePosition >= this.scenarioRecording?.markers[markerIndex].position;
					}
					return (
						this.archivePosition >= this.scenarioRecording?.markers[markerIndex].position &&
						this.archivePosition < this.scenarioRecording?.markers[markerIndex + 1].position
					);
				}
			}
		}
	}

	markerPaneClicked(marker: Marker) {
		if (this.isRecording) {
			this.setMarkerToEditMode(marker);
		}
	}

	focusOutMarker() {
		if (this.allowMarkersOutOfFocus) {
			this.setMarkerToEditMode(null);
		}
	}

	ngOnDestroy(): void {
		console.debug("destroy");
	}

	disableFilterForIcon(iconName: string) {
		return ICONS_WITHOUT_FILTER.some((value) => value === iconName);
	}

	hasRightsToEditMarker(marker: Marker) {
		return (
			marker.userId === this.user?.id ||
			this._simPermissionService.HasDataPerm(this.scenarioRecording, DataPermissions.Edit, DataPermissionType.Bookmarks)
		);
	}

	hasRightsToDeleteMarker(marker: Marker) {
		return (
			marker.userId === this.user?.id ||
			this._simPermissionService.HasDataPerm(this.scenarioRecording, DataPermissions.Delete, DataPermissionType.Bookmarks)
		);
	}
}
